import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Heart, ShoppingCart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { API_URL } from '../config';

const FavoritesScreen = () => {
  const { user, toggleFavorite, getConfig } = useAuth();
  const { addToCart } = useCart();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/users/favorites`, getConfig());
        setFavorites(data);
      } catch (err) {
        console.error('Error fetching favorites:', err);
      } finally {
        setLoading(false);
      }
    };
    if (user) fetchFavorites();
  }, [user, getConfig]);

  const handleRemove = async (productId) => {
    try {
      const updatedFavorites = await toggleFavorite(productId);
      // Since toggleFavorite returns the full list of populated products or just IDs?
      // Actually, my backend returns populated products for getFavorites, 
      // but toggleFavorite returns populated products too.
      setFavorites(updatedFavorites);
    } catch (err) {
      console.error('Error removing favorite:', err);
    }
  };


  return (
    <div className="container mx-auto px-4 py-12 max-w-6xl">
      <div className="space-y-2 mb-10">
        <h1 className="text-3xl font-bold text-secondary-900">My Favorites</h1>
        <div className="h-1.5 w-20 bg-red-400 rounded-full" />
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {[1, 2, 3, 4].map((i) => <div key={i} className="bg-secondary-100 animate-pulse rounded-2xl aspect-[4/5]" />)}
        </div>
      ) : favorites.length === 0 ? (
        <div className="text-center py-20 space-y-4">
          <Heart className="h-16 w-16 text-secondary-200 mx-auto" />
          <h2 className="text-2xl font-bold text-secondary-400">No favorites yet</h2>
          <p className="text-secondary-500">Products you love will appear here.</p>
          <Link to="/" className="inline-block bg-primary-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-primary-700 transition-all">
            Browse Products
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {favorites.map((product) => (
            <div key={product._id} className="bg-white rounded-2xl border border-secondary-100 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
              <Link to={`/product/${product._id}`}>
                <div className="relative aspect-square overflow-hidden">
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <button onClick={(e) => { e.preventDefault(); handleRemove(product._id); }}
                    className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm p-2 rounded-full hover:bg-red-50 transition-colors">
                    <Heart className="h-4 w-4 text-red-500 fill-current" />
                  </button>
                </div>
              </Link>
              <div className="p-5">
                <Link to={`/product/${product._id}`}>
                  <h3 className="text-lg font-bold text-secondary-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-1">{product.name}</h3>
                </Link>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xl font-bold text-secondary-900">₹{product.price}</span>
                  <button onClick={() => addToCart(product)} className="bg-primary-600 hover:bg-primary-700 text-white p-2.5 rounded-xl transition-colors shadow-lg shadow-primary-200">
                    <ShoppingCart className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FavoritesScreen;
